import { useState } from "react";
import { api } from "@/lib/api-client";

// ─── R-54 / WU-3.5 ────────────────────────────────────────────────────────────
//
// The Test button has two call modes so the editor can test connections
// before the destination is persisted:
//
//   1. Saved destination — `destinationId` is a real DB cuid; the route
//      reads the row's adapterType + config and tests those. We POST
//      no body (the route ignores it for saved IDs).
//
//   2. Draft destination — caller passes `destinationId="draft"` plus the
//      in-memory `draftConfig` + `draftAdapterType`. The route resolves
//      `adapter.testConnection(config)` directly without any DB lookup.
//
// The editor always supplies draftConfig / draftAdapterType while typing.
// Callers can still pass a real `destinationId` to exercise the saved
// path (used by tests, and would be used by an "admin re-test a published
// destination" affordance if we ever build one).

interface Props {
  jobTypeId: string;
  destinationId: string;
  /**
   * In-memory adapter config from the editor. Required when
   * `destinationId === "draft"`; ignored for saved IDs.
   */
  draftConfig?: Record<string, unknown>;
  /**
   * In-memory adapter type from the editor. Required when
   * `destinationId === "draft"`; ignored for saved IDs.
   */
  draftAdapterType?: string;
}

export function TestConnectionButton({
  jobTypeId,
  destinationId,
  draftConfig,
  draftAdapterType,
}: Props) {
  // F-035 — `notSupported` is a distinct outcome from `fail`: the adapter never
  // ran a test (e.g. dossier), so it must read as neutral, not as a rejection.
  const [status, setStatus] = useState<
    "idle" | "testing" | "ok" | "fail" | "notSupported"
  >("idle");
  const [message, setMessage] = useState("");

  const handleTest = async () => {
    setStatus("testing");
    setMessage("");

    try {
      const body =
        destinationId === "draft"
          ? { config: draftConfig ?? {}, adapterType: draftAdapterType }
          : undefined;

      const res = await api.post<{
        ok: boolean;
        message: string;
        notSupported?: boolean;
      }>(
        `/job-types/${jobTypeId}/export-destinations/${destinationId}/test`,
        body,
      );

      if (res.ok) {
        setStatus("ok");
      } else if (res.notSupported) {
        setStatus("notSupported");
      } else {
        setStatus("fail");
      }
      setMessage(res.message);
    } catch (err) {
      setStatus("fail");
      setMessage(err instanceof Error ? err.message : "Test failed");
    }
  };

  return (
    <span className="test-connection" style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <button
        className="rb-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleTest();
        }}
        disabled={status === "testing"}
        style={{ fontSize: 12, padding: "2px 10px" }}
      >
        {status === "testing" ? "Testing..." : "Test"}
      </button>
      {status === "ok" && (
        <span className="test-connection-result test-connection-ok">OK</span>
      )}
      {status === "notSupported" && (
        // F-035 — neutral state, not red. Delivery is validated at export time.
        <span
          className="test-connection-result test-connection-not-supported"
          style={{ color: "var(--color-text-muted)" }}
        >
          Not testable
        </span>
      )}
      {status === "fail" && (
        <span
          className="test-connection-result test-connection-fail"
          style={{ color: "var(--color-error)" }}
        >
          Failed
        </span>
      )}
      {/* F-027 — surface the server's message INLINE, not only in a tooltip, so
          a failed test isn't a dead end. Shown for fail + notSupported. */}
      {message && (status === "fail" || status === "notSupported") && (
        <span
          className="test-connection-message"
          role="status"
          style={{
            fontSize: 12,
            color:
              status === "fail"
                ? "var(--color-error)"
                : "var(--color-text-muted)",
          }}
        >
          {message}
        </span>
      )}
    </span>
  );
}
